import { useMemo, useState } from 'react'
import { canScheduleFor, useCompanies, useDirectory, useMeetings } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { MeetingDrawer } from '../components/MeetingDrawer'
import { Avatar } from '../components/TaskRow'
import type { Meeting } from '../api/types'
import {
  WEEKDAY_LABELS,
  addMonths,
  dayKey,
  formatDate,
  formatDayHeading,
  formatMonthLabel,
  formatTime,
  formatTimeRange,
  localZoneLabel,
  monthGrid,
  monthGridRange,
  startOfMonth,
} from '../lib/dates'
import {
  Button,
  Chip,
  EmptyState,
  ErrorNotice,
  Loading,
  MeetingStatusPill,
  PageHeader,
  TabStrip,
  cx,
} from '../components/ui'

type View = 'calendar' | 'list'

/** How far ahead the chronological list looks. Long enough to plan, short enough to read. */
const LIST_DAYS = 90

/** Meetings shown in a day cell before the cell offers to expand. */
const CELL_LIMIT = 3

const TABS = [
  { id: 'calendar', label: 'calendar' },
  { id: 'list', label: 'list' },
] as const

/**
 * A month grid is unusable on a phone, so small screens open on the list and
 * the grid itself is hidden below `sm`. This is the same reasoning that keeps
 * the board's columns behind a confined scroller.
 */
function defaultView(): View {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'list'
  return window.matchMedia('(min-width: 640px)').matches ? 'calendar' : 'list'
}

export function MeetingsPage() {
  const { user } = useAuth()
  const [view, setView] = useState<View>(defaultView)
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()))
  const [companyId, setCompanyId] = useState('')
  const [personId, setPersonId] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)
  const [drawer, setDrawer] = useState<
    { mode: 'view'; meetingId: string } | { mode: 'create'; date: string } | null
  >(null)

  const { data: companies } = useCompanies()
  const { data: people } = useDirectory()

  const canSchedule = useMemo(
    () => (user ? (companies ?? []).some((c) => canScheduleFor(c, user)) : false),
    [companies, user],
  )

  const monthRange = useMemo(() => monthGridRange(anchor), [anchor])

  // The list window is fixed for the session, so scrolling it does not refetch.
  const listRange = useMemo(() => {
    const from = new Date()
    from.setHours(0, 0, 0, 0)
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + LIST_DAYS)
    return { from: from.toISOString(), to: to.toISOString() }
  }, [])

  const range = view === 'calendar' ? monthRange : listRange

  const { data: meetings, isLoading, error } = useMeetings({
    from: range.from,
    to: range.to,
    companyId: companyId || undefined,
    userId: personId || undefined,
    includeCancelled,
  })

  // Bucketed by the day a meeting starts, which is the key the grid cells and
  // the list headings both look up.
  const byDay = useMemo(() => {
    const grouped = new Map<string, Meeting[]>()
    for (const meeting of meetings ?? []) {
      const key = dayKey(meeting.startsAt)
      const bucket = grouped.get(key)
      if (bucket) bucket.push(meeting)
      else grouped.set(key, [meeting])
    }
    return grouped
  }, [meetings])

  const zone = localZoneLabel()
  const days = useMemo(() => monthGrid(anchor), [anchor])

  function openMeeting(id: string) {
    setDrawer({ mode: 'view', meetingId: id })
  }

  return (
    <>
      <PageHeader
        title="Meetings"
        subtitle={
          zone
            ? `Times shown in your local time (${zone}).`
            : 'Times shown in your local time.'
        }
        actions={
          canSchedule ? (
            <Button
              variant="primary"
              onClick={() => setDrawer({ mode: 'create', date: dayKey(new Date()) })}
            >
              New meeting
            </Button>
          ) : undefined
        }
      />

      <TabStrip tabs={TABS} active={view} onChange={setView} />

      <div className="card mb-5 grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
        <select
          className="field"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {companies?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="field"
          value={personId}
          onChange={(e) => setPersonId(e.target.value)}
          aria-label="Filter by attendee"
        >
          <option value="">Everyone</option>
          <option value="me">Just me</option>
          {people?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 px-1 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setIncludeCancelled(e.target.checked)}
            className="rounded border-slate-300 dark:border-slate-600"
          />
          Show cancelled
        </label>
      </div>

      {view === 'calendar' && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setAnchor((a) => addMonths(a, -1))}>
            Previous
          </Button>
          <Button size="sm" onClick={() => setAnchor((a) => addMonths(a, 1))}>
            Next
          </Button>
          <Button size="sm" onClick={() => setAnchor(startOfMonth(new Date()))}>
            Today
          </Button>
          <h2 className="ml-1 text-base font-semibold tracking-tight">
            {formatMonthLabel(anchor)}
          </h2>
        </div>
      )}

      {isLoading && <Loading />}
      {error && <ErrorNotice error={error} />}

      {view === 'calendar' && (
        <>
          <p className="card px-4 py-6 text-center text-sm text-slate-500 sm:hidden dark:text-slate-400">
            The month grid needs a wider screen. Switch to List to see these meetings on a phone.
          </p>

          {/* Any sideways scrolling is confined to the grid, exactly as the
              board confines its columns, so the page itself never moves. */}
          <div className="hidden overflow-x-auto pb-2 sm:block">
            <div className="card min-w-[44rem] overflow-hidden">
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                {WEEKDAY_LABELS.map((label) => (
                  <div
                    key={label}
                    className="px-2 py-1.5 text-center text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400"
                  >
                    {label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {days.map((day) => (
                  <DayCell
                    key={day.key}
                    date={day.date}
                    inMonth={day.inMonth}
                    isToday={day.isToday}
                    meetings={byDay.get(day.key) ?? []}
                    canSchedule={canSchedule}
                    onOpenMeeting={openMeeting}
                    onAddOnDay={(value) => setDrawer({ mode: 'create', date: value })}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {view === 'list' && meetings && meetings.length > 0 && (
        <div className="space-y-5">
          {[...byDay.entries()].map(([key, dayMeetings]) => {
            const first = dayMeetings[0]
            if (!first) return null
            return (
              <section key={key}>
                <h2 className="mb-2 px-1 text-sm font-semibold">
                  {formatDayHeading(first.startsAt)}
                </h2>
                <div className="card divide-y divide-slate-100 overflow-hidden dark:divide-slate-800">
                  {dayMeetings.map((meeting) => (
                    <MeetingListRow key={meeting.id} meeting={meeting} onOpen={openMeeting} />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {/* The calendar says "empty" by being empty, so this is the list's job. */}
      {view === 'list' && meetings && meetings.length === 0 && (
        <EmptyState
          title="Nothing booked"
          description={
            canSchedule
              ? 'Nothing in this window. Try another month, clear a filter, or book a meeting.'
              : 'Nothing in this window. Try another month or clear a filter.'
          }
        />
      )}

      {drawer && (
        <MeetingDrawer
          key={drawer.mode === 'view' ? drawer.meetingId : `create-${drawer.date}`}
          meetingId={drawer.mode === 'view' ? drawer.meetingId : null}
          initialDate={drawer.mode === 'create' ? drawer.date : undefined}
          onClose={() => setDrawer(null)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function DayCell({
  date,
  inMonth,
  isToday,
  meetings,
  canSchedule,
  onOpenMeeting,
  onAddOnDay,
}: {
  date: Date
  inMonth: boolean
  isToday: boolean
  meetings: Meeting[]
  canSchedule: boolean
  onOpenMeeting: (id: string) => void
  onAddOnDay: (dateValue: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? meetings : meetings.slice(0, CELL_LIMIT)
  const hidden = meetings.length - shown.length

  return (
    <div
      className={cx(
        'flex min-h-28 flex-col border-r border-b border-slate-100 last:border-r-0 dark:border-slate-800',
        inMonth ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-950/40',
      )}
    >
      <div className="flex items-center justify-between px-1.5 pt-1.5">
        <span
          className={cx(
            'grid h-5 min-w-5 place-items-center rounded-full px-1 text-xs tabular-nums',
            isToday && 'bg-brand-600 font-semibold text-white dark:bg-brand-500',
            !isToday && inMonth && 'text-slate-600 dark:text-slate-300',
            !isToday && !inMonth && 'text-slate-400 dark:text-slate-600',
          )}
        >
          {date.getDate()}
        </span>
      </div>

      <div className="flex flex-col gap-0.5 px-1 pt-1">
        {shown.map((meeting) => (
          <button
            key={meeting.id}
            type="button"
            onClick={() => onOpenMeeting(meeting.id)}
            title={`${formatTimeRange(meeting.startsAt, meeting.endsAt)} ${meeting.title}`}
            className={cx(
              'truncate rounded px-1 py-0.5 text-left text-xs transition-colors',
              meeting.status === 'CANCELLED'
                ? 'text-slate-400 line-through hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800'
                : 'bg-brand-50 text-brand-800 hover:bg-brand-100 dark:bg-brand-900/40 dark:text-brand-100 dark:hover:bg-brand-900/70',
            )}
          >
            <span className="tabular-nums">{formatTime(meeting.startsAt)}</span> {meeting.title}
          </button>
        ))}

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="px-1 text-left text-xs text-slate-500 hover:underline dark:text-slate-400"
          >
            {hidden === 1 ? '1 more' : `${hidden} more`}
          </button>
        )}

        {expanded && meetings.length > CELL_LIMIT && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="px-1 text-left text-xs text-slate-500 hover:underline dark:text-slate-400"
          >
            Show less
          </button>
        )}
      </div>

      {canSchedule ? (
        <button
          type="button"
          onClick={() => onAddOnDay(dayKey(date))}
          aria-label={`Book a meeting on ${formatDate(date.toISOString())}`}
          className="mt-auto min-h-6 w-full rounded-sm transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/30"
        />
      ) : (
        <div className="mt-auto min-h-6" />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function MeetingListRow({
  meeting,
  onOpen,
}: {
  meeting: Meeting
  onOpen: (id: string) => void
}) {
  const cancelled = meeting.status === 'CANCELLED'

  return (
    <button
      type="button"
      onClick={() => onOpen(meeting.id)}
      className="row-alt flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
    >
      <span className="w-32 shrink-0 pt-0.5 text-xs tabular-nums text-slate-500 dark:text-slate-400">
        {formatTimeRange(meeting.startsAt, meeting.endsAt)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={cx(
              'text-sm font-medium',
              cancelled && 'text-slate-400 line-through dark:text-slate-500',
            )}
          >
            {meeting.title}
          </span>
          <Chip tone="accent">{meeting.company.shortName ?? meeting.company.name}</Chip>
          <MeetingStatusPill status={meeting.status} />
        </span>

        {meeting.location && (
          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
            {meeting.location}
          </span>
        )}
      </span>

      <span className="flex shrink-0 -space-x-1.5">
        {meeting.attendees.slice(0, 5).map((person) => (
          <Avatar key={person.id} name={person.name} />
        ))}
        {meeting.attendees.length > 5 && (
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-slate-100 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {meeting.attendees.length - 5}
          </span>
        )}
      </span>
    </button>
  )
}
